import React from 'react'
import styled from '@emotion/styled'
import { colors, radius, shadow, spacing, font } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { useExpandToggle } from '@mobi/utils/hooks/useExpandToggle'

type AccordionProps = {
  title: React.ReactNode
  children: React.ReactNode
  contentLeft?: React.ReactNode
  contentRight?: React.ReactNode
  /** Triggered when the accordion is toggled */
  onToggle?: (isExpandedPrevious: boolean) => void
  /** Determines if the accordion starts off already expanded */
  shouldStartExpanded?: boolean
  /** Controls if main theme should used */
  theme?: 'primary' | 'secondary' | null
  /** Changes component to controlled with external state */
  isExpandedControlled?: boolean
}

export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  (
    {
      title,
      children,
      contentLeft,
      contentRight,
      onToggle,
      shouldStartExpanded,
      theme = 'secondary',
      isExpandedControlled,
    },
    ref
  ) => {
    const id = React.useId()
    const isFirstRenderRef = React.useRef(true)

    const { wrapperRef, contentRef, shouldRender, isExpanded, toggleExpanded } = useExpandToggle({
      timeoutMs: 400,
      shouldStartExpanded,
    })

    React.useEffect(() => {
      if (typeof isExpandedControlled !== 'boolean') return
      toggleExpanded(isExpandedControlled)
    }, [isExpandedControlled, toggleExpanded])

    // If shouldStartExpanded, prevent animation on mount
    React.useLayoutEffect(() => {
      if (!isFirstRenderRef.current || !shouldStartExpanded || !wrapperRef.current) return
      wrapperRef.current.style.height = 'auto'
      wrapperRef.current.style.opacity = '1'
      isFirstRenderRef.current = false
    }, [shouldStartExpanded, wrapperRef])

    // Handle content opacity toggling
    React.useEffect(() => {
      if (!wrapperRef.current) return
      wrapperRef.current.style.opacity = isExpanded ? '1' : '0'
    }, [wrapperRef, isExpanded])

    const buttonId = `${id}.button`
    const regionId = `${id}.region`

    return (
      <ContainerStyled
        ref={ref}
        data-theme={theme}
        data-is-expanded={isExpanded}
        data-has-content-left={!!contentLeft}
        data-testid='Accordion'
      >
        <header
          onClick={() => {
            onToggle?.(isExpanded)
            toggleExpanded()
          }}
          aria-expanded={isExpanded}
          aria-controls={regionId}
          id={buttonId}
          data-testid='Accordion.Header'
        >
          <span>
            {contentLeft}

            <span className='accordion-head__title'>{title}</span>
          </span>

          <span>
            {contentRight}

            <Icon
              className='accordion-head__chevron'
              size='2.4rem'
              name='SolidChevronDown'
              color='inherit'
            />
          </span>
        </header>

        {(isExpanded || shouldRender) && (
          <section ref={wrapperRef} aria-labelledby={buttonId} id={regionId} role='region'>
            <div ref={contentRef} data-testid='Accordion.Content'>
              {children}
            </div>
          </section>
        )}
      </ContainerStyled>
    )
  }
)

Accordion.displayName = 'Accordion'

// ======
// Styles
// ======

const ContainerStyled = styled.article({
  '> header': {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.smx2,
    width: '100%',
    minHeight: '4.8rem',
    padding: `0 ${spacing.md}`,
    background: 0,
    border: 0,
    WebkitTapHighlightColor: colors.transparent,
    touchAction: 'manipulation',
    textAlign: 'left',

    '&:hover': { cursor: 'pointer' },

    '> span': {
      display: 'flex',
      alignItems: 'center',

      '&:first-of-type': {
        justifyContent: 'flex-start',
      },

      '&:last-of-type': {
        justifyContent: 'flex-end',
        gap: spacing.smx2,
      },
    },

    '.accordion-head__title': {
      flex: 1,
      fontFamily: font.family.primary,
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      color: colors.black,
      fontWeight: font.weight.medium,
    },

    '.accordion-head__chevron': {
      transform: 'rotate(0deg)',
      transition: 'all 400ms ease',
      color: colors.neutral[900],
    },
  },

  '> section': {
    overflow: 'hidden',
    height: 0,
    opacity: 0,
    transition: 'height 400ms ease, opacity 600ms ease',
    willChange: 'height',

    '> div': { paddingInline: spacing.md },
  },

  '&[data-is-expanded="true"]': {
    '> header .accordion-head__chevron': { transform: 'rotate(-180deg)' },
  },

  '&[data-theme="primary"]': {
    position: 'relative',
    borderBottom: `0.1rem solid ${colors.neutral[400]}`,

    '> section': {
      '> div': { paddingBottom: spacing.sm },
    },
  },

  '&[data-theme="secondary"]': {
    backgroundColor: colors.white,
    border: `1px solid ${colors.neutral[300]}`,
    borderRadius: radius.lgx3,
    boxShadow: shadow.xs,

    '> section': {
      borderTop: `0.1rem solid ${colors.neutral[300]}`,

      '> div': { paddingBlock: spacing.sm },
    },
  },

  '&[data-has-content-left="true"]': {
    '> header': {
      paddingLeft: spacing.smx2,

      '> span:first-of-type': { gap: '0.2rem' },
    },
  },
})
